import { useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'

// Activity events to monitor
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
  'keydown'
] as const

interface UseAutoLogoutOptions {
  timeoutMinutes?: number
  warningMinutes?: number
  onWarning?: () => void
  onLogout?: () => void
  enabled?: boolean
}

export const useAutoLogout = ({
  timeoutMinutes = 15,
  warningMinutes = 1,
  onWarning,
  onLogout,
  enabled = true
}: UseAutoLogoutOptions = {}) => {
  const { isAuthenticated, logoutDueToInactivity } = useAuthStore()
  const timeoutRef = useRef<NodeJS.Timeout>()
  const warningTimeoutRef = useRef<NodeJS.Timeout>()
  const lastActivityRef = useRef<number>(Date.now())

  // Convert minutes to milliseconds
  const timeoutMs = timeoutMinutes * 60 * 1000
  const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000

  const resetTimer = useCallback(() => {
    if (!enabled || !isAuthenticated) {
      // console.log('🔐 Auto-logout: Timer not started - enabled:', enabled, 'authenticated:', isAuthenticated)
      return
    }

    lastActivityRef.current = Date.now()
    // console.log('🔐 Auto-logout: Timer reset at', new Date().toISOString())

    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }

    // Set warning timer
    warningTimeoutRef.current = setTimeout(() => {
      // console.log('🔐 Auto-logout: Warning triggered at', new Date().toISOString())
      if (onWarning) {
        onWarning()
      }
    }, warningMs)

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      // console.log('🔐 Auto-logout: User inactive for', timeoutMinutes, 'minutes - logging out at', new Date().toISOString())
      logoutDueToInactivity()
      if (onLogout) {
        onLogout()
      }
    }, timeoutMs)
  }, [enabled, isAuthenticated, warningMs, timeoutMs, timeoutMinutes, onWarning, onLogout, logoutDueToInactivity])

  const handleActivity = useCallback(() => {
    if (!enabled || !isAuthenticated) return
    resetTimer()
  }, [enabled, isAuthenticated, resetTimer])

  const extendSession = useCallback(() => {
    if (!enabled || !isAuthenticated) return
    
    // Clear warning timeout since user chose to stay
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }
    
    resetTimer()
  }, [enabled, isAuthenticated, resetTimer])

  const logoutNow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }
    
    logoutDueToInactivity()
    if (onLogout) {
      onLogout()
    }
  }, [logoutDueToInactivity, onLogout])

  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      // Clear timers if not enabled or not authenticated
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
      return
    }

    // Initialize timer
    resetTimer()

    // Add event listeners for activity detection
    const addEventListeners = () => {
      ACTIVITY_EVENTS.forEach(event => {
        document.addEventListener(event, handleActivity, { passive: true })
      })
    }

    const removeEventListeners = () => {
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
    }

    addEventListeners()

    return () => {
      removeEventListeners()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
    }
  }, [enabled, isAuthenticated, handleActivity, resetTimer])

  return {
    extendSession,
    logoutNow,
    timeoutMinutes,
    warningMinutes
  }
} 